import React, { useEffect, useRef, useState } from "react";
import { ColorV2 } from "config/colors";
import { CustomIcon } from "components/icon/customIcon.component";
import SimflexityLogo from "images/icons/svg/v2/simflexity-logo.svg";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Badge } from "components/buttons/buttons.styled";
import { useAuthStore } from "stores/authStore/authStore";
import { CompanyRole } from "model/User";
import {
  Container,
  IconWrap,
  ItemButton,
  ItemLink,
  Items,
  ItemsTrack,
  type ItemsTrackLayout,
  Label,
  Logo,
  MeasureItem,
  MeasureTrack,
} from "./navbarMenu.styles";

interface BaseMenuItem {
  readonly badgeCount?: number;
  readonly iconName: string;
  readonly label: string;
  readonly value: string;
}

export type MenuItem =
  | (BaseMenuItem & {
      readonly href: string;
      readonly kind: "link";
    })
  | (BaseMenuItem & {
      readonly kind: "action";
    });

interface MenuProps {
  readonly activeValue: string;
  readonly alignItemsToEnd?: boolean;
  readonly items: readonly MenuItem[];
  readonly logoSrc?: string;
  readonly onItemClick?: (
    item: MenuItem,
    event: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>,
  ) => void;
}

interface NavbarMenuItemProps {
  readonly fillAvailableSpace: boolean;
  readonly isActive: boolean;
  readonly item: MenuItem;
  readonly showLabel: boolean;
  readonly onClick: (
    event: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>,
  ) => void;
}

const NavbarMenuItem = ({
  item,
  isActive,
  fillAvailableSpace,
  onClick,
  showLabel,
}: NavbarMenuItemProps): React.ReactElement => {
  const [isHighlighted, setIsHighlighted] = useState(false);
  const isAction = item.kind === "action";
  const isDarkBlue = isActive || isAction;
  const ariaCurrent: "page" | undefined = isActive ? "page" : undefined;
  const iconColor = isHighlighted
    ? ColorV2.Blue3
    : isDarkBlue
      ? ColorV2.Blue1
      : ColorV2.Blue5;
  const isExpanded = !showLabel && isHighlighted;
  const menuItemContent = (
    <>
      <IconWrap>
        <CustomIcon name={item.iconName} size="24px" color={iconColor} />
        {typeof item.badgeCount === "number" && item.badgeCount > 0 && (
          <Badge>{item.badgeCount}</Badge>
        )}
      </IconWrap>
      <Label $isExpanded={isExpanded} $showLabel={showLabel}>
        {item.label}
      </Label>
    </>
  );

  const sharedProps = {
    "aria-label": item.label,
    "aria-current": ariaCurrent,
    $fillAvailableSpace: fillAvailableSpace,
    $isActive: isActive,
    $isAction: isAction,
    $isExpanded: isExpanded,
    $showLabel: showLabel,
    onBlur: () => setIsHighlighted(false),
    onClick,
    onFocus: () => setIsHighlighted(true),
    onMouseEnter: () => setIsHighlighted(true),
    onMouseLeave: () => setIsHighlighted(false),
  };

  if (item.kind === "action") {
    return (
      <ItemButton type="button" {...sharedProps}>
        {menuItemContent}
      </ItemButton>
    );
  }

  return (
    <ItemLink to={item.href} {...sharedProps}>
      {menuItemContent}
    </ItemLink>
  );
};

export const NavbarMenu = ({
  items,
  activeValue,
  alignItemsToEnd = false,
  logoSrc = SimflexityLogo,
  onItemClick,
}: MenuProps): React.ReactElement => {
  const { t } = useTranslation();
  const [authState] = useAuthStore();
  const itemsRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const [showLabels, setShowLabels] = useState(true);
  const itemsTrackLayout: ItemsTrackLayout = alignItemsToEnd
    ? { $layout: "end" }
    : { $layout: "spread", $showLabels: showLabels };

  useEffect((): (() => void) | void => {
    if (typeof ResizeObserver === "undefined") return undefined;

    const itemsElement = itemsRef.current;
    const measureElement = measureRef.current;

    if (itemsElement == null || measureElement == null) return undefined;

    const updateLabelVisibility = (): void => {
      const availableWidth = itemsElement.clientWidth;
      const requiredWidth = measureElement.scrollWidth;
      setShowLabels(requiredWidth <= availableWidth);
    };

    updateLabelVisibility();

    const resizeObserver = new ResizeObserver(() => {
      updateLabelVisibility();
    });

    resizeObserver.observe(itemsElement);
    resizeObserver.observe(measureElement);

    return (): void => {
      resizeObserver.disconnect();
    };
  }, [items]);

  return (
    <Container role="navigation" aria-label={t("DesignComponents.MainMenu")}>
      {authState.user?.role !== CompanyRole.COMPANY_TIMESTAMP && (
        <Link to="/company/schedule">
          <Logo src={logoSrc} alt="Simflexity logo" />
        </Link>
      )}
      {authState.user?.role === CompanyRole.COMPANY_TIMESTAMP && (
        <Link to="/company/stampclock">
          <Logo src={logoSrc} alt="Simflexity logo" />
        </Link>
      )}
      <Items
        ref={itemsRef}
        $shouldAlignItemsToEnd={alignItemsToEnd}
        $showLabels={showLabels}
      >
        <ItemsTrack {...itemsTrackLayout}>
          {items.map((item) => (
            <NavbarMenuItem
              key={item.value}
              fillAvailableSpace={
                showLabels && itemsTrackLayout.$layout === "spread"
              }
              item={item}
              isActive={item.value === activeValue}
              onClick={(event) => onItemClick?.(item, event)}
              showLabel={showLabels}
            />
          ))}
        </ItemsTrack>
      </Items>
      <MeasureTrack ref={measureRef} aria-hidden>
        {items.map((item) => (
          <MeasureItem key={item.value}>
            <CustomIcon
              name={item.iconName}
              size="24px"
              color={ColorV2.Blue5}
            />
            <span>{item.label}</span>
          </MeasureItem>
        ))}
      </MeasureTrack>
    </Container>
  );
};
